import InputDecimalCell from '@/components/_common/InputDecimalCell';
import { usePurchaseItemsSellPriceUpdate } from '@/core/domain/purchase/purchaseItem/PurchaseItem.hooks';
import { PurchaseItem } from '@/core/domain/purchase/purchaseItem/PurchaseItem.types';
import { useDisclosure } from '@/hooks';
import Authorities from '@/types/Authority';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import { quantityFormatter } from '@/utils/number';
import PurchaseItemSpan from '../PurchaseItemSpan';

interface Props {
  item: PurchaseItem;
}

export default function PurchaseItemSellPriceIncrease(props: Props) {
  const [isInputEnabled, inputAction] = useDisclosure(false);
  const purchaseItemSellPriceUpdate = usePurchaseItemsSellPriceUpdate();

  const baseValue = props.item.unitaryValue;

  function calculateSalePriceByIncreasePercent(
    baseValue: number,
    increasePercent: number,
  ): number {
    if (!Number.isFinite(baseValue) || !Number.isFinite(increasePercent)) {
      return 0;
    }

    return baseValue + baseValue * (increasePercent / 100);
  }

  function calculateIncreasePercentBySalePrice(
    baseValue: number,
    salePrice: number,
  ): number {
    if (
      !Number.isFinite(baseValue) ||
      baseValue === 0 ||
      !Number.isFinite(salePrice)
    ) {
      return 0;
    }

    return ((salePrice - baseValue) / baseValue) * 100;
  }

  const increasePercent = calculateIncreasePercentBySalePrice(
    baseValue,
    props.item.sellPriceInformed ?? 0,
  );

  const handleConfirmValue = async (value: number) => {
    try {
      const sellPriceInformed = calculateSalePriceByIncreasePercent(
        baseValue,
        value,
      );
      await purchaseItemSellPriceUpdate.mutateAsync([
        { id: props.item.id, sellPriceInformed },
      ]);
    } finally {
      inputAction.close();
    }
  };

  if (isInputEnabled) {
    return (
      <InputDecimalCell
        defaultValue={increasePercent}
        min={0}
        loading={purchaseItemSellPriceUpdate.isLoading}
        onConfirmValue={handleConfirmValue}
        onClose={inputAction.close}
      />
    );
  }

  return (
    <PurchaseItemSpan
      onClick={inputAction.open}
      hasPermission={hasPermissions([
        Authorities.MOVIMENTATION_PURCHASE_ITEM_EDIT_SELL_PRICE,
      ])}
    >
      {`${quantityFormatter(increasePercent)}%`}
    </PurchaseItemSpan>
  );
}
